import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Wallet } from 'lucide-react';
import { portalSettlements } from '../../api/resources';
import { inr, fmtDate } from '../../utils/format';
import PageHeader from '../../components/ui/PageHeader';
import Spinner from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';
import { StatusBadge } from '../../components/ui/Badge';
import { WORK_LABEL } from './shared';

export default function PortalPayouts() {
  const { data, isLoading } = useQuery({ queryKey: ['portal-settlements'], queryFn: portalSettlements });
  const [open, setOpen] = useState(null);
  const list = data?.settlements || [];

  return (
    <div className="space-y-4">
      <PageHeader icon={Wallet} eyebrow="My Work" title="My Payouts" subtitle="Every settlement paid to you, itemised" />
      {isLoading && <Spinner />}
      {!isLoading && list.length === 0 && (
        <div className="card"><EmptyState title="No payouts yet" hint="Settlements appear here once your administrator pays out completed work." /></div>
      )}
      {list.map((s) => {
        const expanded = open === s.id;
        return (
          <div key={s.id} className="card overflow-hidden">
            <button
              className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-surface-hover"
              onClick={() => setOpen(expanded ? null : s.id)}
              aria-expanded={expanded}
            >
              <span className="font-semibold text-brand-700">{s.settlementNo}</span>
              <StatusBadge status={s.status} />
              <span className="text-xs text-content-subtle">{fmtDate(s.createdAt)}</span>
              {s.periodTo && (
                <span className="text-xs text-content-subtle">
                  period {s.periodFrom ? `${fmtDate(s.periodFrom)} – ` : 'up to '}{fmtDate(s.periodTo)}
                </span>
              )}
              <span className={`ml-auto text-lg font-bold tabular-nums ${s.status === 'REVERSED' ? 'text-content-subtle line-through' : 'text-brand-900'}`}>
                {inr(s.status === 'REVERSED' ? s.grossAmount : s.amount)}
              </span>
              <ChevronDown size={16} className={`text-content-subtle transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
            {expanded && (
              <div className="border-t border-line px-4 py-3">
                {(s.paymentMode || s.reference) && (
                  <p className="mb-2 text-xs text-content-muted">
                    {s.paymentMode && <>Paid by <b>{s.paymentMode}</b></>}{s.reference && <> · Ref {s.reference}</>}
                  </p>
                )}
                <table className="w-full text-sm">
                  <thead>
                    <tr><th className="th">Order</th><th className="th">Item</th><th className="th">Work</th><th className="th text-right">Amount</th></tr>
                  </thead>
                  <tbody>
                    {s.lines.map((l, i) => (
                      <tr key={i} className="row">
                        <td className="td">{l.orderNo}</td>
                        <td className="td">{l.itemName} × {l.qty}</td>
                        <td className="td text-content-muted">{WORK_LABEL[l.workType]}</td>
                        <td className={`td text-right tabular-nums ${l.status === 'RELEASED' ? 'text-content-subtle line-through' : ''}`}>
                          {inr(l.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {s.status === 'REVERSED' && (
                  <p className="mt-2 text-xs text-danger-600">This settlement was reversed; its work is payable again.</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
